import { useCallback, useEffect, useRef, useState } from "react";
import {
	Button,
	Dropdown,
	Option,
	Text,
	Spinner,
} from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { fetchDeploymentsFn, getTenantId } from "@/repository/queryFns";
import { TenantDeploymentDTO } from "@/repository/models";
import { useAuth } from "@/hooks/authentication";
import { useStyle } from "./style";
import { Flex, GapSize } from "@/components/atoms/flex";
import { StorageKeys } from "@/common/constants/storageKeys";
import { captureError } from "@/common/helpers/sentryHelper";
import { fetchDeploymentsApacFn } from "@/repository/queryFns/deployments";
import {
	DebugInfoActions,
	DebugInfoKey,
	WindowReloadReason,
	useDebugStore,
} from "@/common/stores/persistingStores/debugStore";

export const AuthenticationView = () => {
	const intl = useIntl();
	const classes = useStyle();
	const authContext = useAuth();
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [selectedDeployment, setSelectedDeployment] =
		useState<TenantDeploymentDTO>();
	const [deployments, setDeployments] = useState<TenantDeploymentDTO[]>([]);
	const isLoginProcessingRef = useRef<boolean>(false);
	const isFetchDeploymentsRequestInProgress = useRef<boolean>(false);
	const [isLogInHidden, setisLogInHidden] = useState(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const debugInfoActions = useDebugStore((state: DebugInfoActions) => state);

	useEffect(() => {
		const tenantId = localStorage.getItem(StorageKeys.TIDForAzure);
		if (tenantId == undefined) {
			setDeployments([]);
			setSelectedDeployment(undefined);

			return;
		}
		getTenantDeployments(true, false);
	}, []);

	const getTenantDeployments = async (
		canRetry: boolean = false,
		useApac: boolean = false
	): Promise<TenantDeploymentDTO[]> => {
		let fallbackTimeout: ReturnType<typeof setTimeout> | null = null;

		try {
			isFetchDeploymentsRequestInProgress.current = true;
			setIsLoading(true);
			// Stop loading spinner after 5s max
			setTimeout(() => {
				setIsLoading(false);
			}, 5000);

			// Fallback to APAC after 3s if still in progress
			if (canRetry) {
				fallbackTimeout = setTimeout(() => {
					if (isFetchDeploymentsRequestInProgress.current && !useApac) {
						isFetchDeploymentsRequestInProgress.current = false;
						getTenantDeployments(false, true);
					}
				}, 3000);
			}

			const tenantDeployments = useApac
				? await fetchDeploymentsApacFn()
				: await fetchDeploymentsFn();

			isFetchDeploymentsRequestInProgress.current = false;
			setDeployments(tenantDeployments);

			const cachedDeploymentKey = localStorage.getItem(
				StorageKeys.DeploymentTenantKey
			);

			if (!tenantDeployments || tenantDeployments.length === 0) {
				// clear deployment info if cached
				if (cachedDeploymentKey) {
					clearCachedDeployment(WindowReloadReason.CacheDeploymentNoneFound);
				}

				return tenantDeployments;
			}

			// cache first deployment data if missing
			if (!cachedDeploymentKey) {
				cacheDeployment(
					tenantDeployments[0],
					WindowReloadReason.CacheDeploymentFirstTime
				);
				setSelectedDeployment(tenantDeployments[0]);

				return tenantDeployments;
			}

			// set env cookie if missing, but deployment cached in local storage
			if (
				!document.cookie
					.split(";")
					.some((item) => item.trim().startsWith("env="))
			) {
				document.cookie = `env=${cachedDeploymentKey?.split(":")[0]}; max-age=60480000; SameSite=None; Secure; Partitioned;`;

				// page needs to be reloaded to apply new environment context
				await forceReloadFull(
					WindowReloadReason.CacheDeploymentCookieNoneFound
				);
			}

			// do not show deployments select if only one deployment found
			if (tenantDeployments.length === 1 && tenantDeployments[0]) {
				if (
					cachedDeploymentKey !==
					`${tenantDeployments[0].deployment.id}:${tenantDeployments[0].tenantId}:${tenantDeployments[0].azureTenantId}`
				) {
					cacheDeployment(
						tenantDeployments[0],
						WindowReloadReason.CacheDeploymentSingleDeployment
					);
				}

				return tenantDeployments;
			}

			const cachedDeployment = tenantDeployments.find(
				(x) =>
					`${x.deployment.id}:${x.tenantId}:${x.azureTenantId}` ===
					cachedDeploymentKey
			);

			// deployment does not exists anymore, clear cache
			if (!cachedDeployment) {
				clearCachedDeployment(
					WindowReloadReason.CacheDeploymentNotExistingAnymore
				);

				return tenantDeployments;
			}

			setSelectedDeployment(cachedDeployment);

			return tenantDeployments;
		} catch (error: any) {
			isFetchDeploymentsRequestInProgress.current = false;

			if (canRetry && !useApac) {
				getTenantDeployments(false, true);
			}

			captureError(error, "getTenantDeployments failed");
		} finally {
			if (fallbackTimeout) {
				clearTimeout(fallbackTimeout);
			}

			isFetchDeploymentsRequestInProgress.current = false;
			setIsLoading(false);
		}
		return [];
	};

	const onDeploymentSelectHandler = useCallback(
		(optionValue: string | undefined) => {
			const newSelected = deployments!.find(
				(x) =>
					`${x.deployment.id}:${x.tenantId}:${x.azureTenantId}` === optionValue
			);

			if (!newSelected) {
				clearCachedDeployment(WindowReloadReason.CacheDeploymentNoneSelected);

				return;
			}

			cacheDeployment(
				newSelected,
				WindowReloadReason.CacheDeploymentSelectionMade
			);
		},
		[deployments]
	);

	const forceReloadFull = async (windowReloadReason: WindowReloadReason) => {
		// check if service workers are supported
		if ("serviceWorker" in navigator) {
			try {
				// unregister all service workers
				const regs = await navigator.serviceWorker.getRegistrations();
				for (const reg of regs) {
					await reg.unregister();
				}

				// clear caches too
				const cacheNames = await caches.keys();
				await Promise.all(cacheNames.map((name) => caches.delete(name)));
			} catch (err) {
				console.warn("[forceReloadFull] Failed to unregister SWs:", err);
			}
		} else {
			console.log(
				"[forceReloadFull] Service workers not supported in this browser"
			);
		}

		debugInfoActions.pushDebugInfoItem(
			DebugInfoKey.WindowReloadTriggerEvents,
			windowReloadReason
		);

		// reload fresh from network
		window.location.reload();
	};

	const cacheDeployment = async (
		tenantDeployment: any,
		windowReloadReason: WindowReloadReason
	) => {
		setisLogInHidden(true);

		localStorage.setItem(
			StorageKeys.DeploymentTenantKey,
			`${tenantDeployment.deployment.id}:${tenantDeployment.tenantId}:${tenantDeployment.azureTenantId}`
		);

		localStorage.setItem(
			StorageKeys.DeploymentTenantId,
			tenantDeployment.tenantId
		);

		document.cookie = `env=${tenantDeployment.deployment.id}; max-age=60480000; SameSite=None; Secure; Partitioned;`;

		setisLogInHidden(false);

		// page needs to be reloaded to apply new environment context
		await forceReloadFull(windowReloadReason);
	};

	const clearCachedDeployment = (windowReloadReason: WindowReloadReason) => {
		document.cookie =
			"env=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure; Partitioned;";
		localStorage.removeItem(StorageKeys.DeploymentTenantKey);
		localStorage.removeItem(StorageKeys.DeploymentTenantId);

		debugInfoActions.pushDebugInfoItem(
			DebugInfoKey.WindowReloadTriggerEvents,
			windowReloadReason
		);

		setTimeout(() => {
			window.location.reload();
		}, 300);
	};

	const signInHandler = useCallback(async () => {
		if (isLoginProcessingRef.current) {
			return;
		}

		isLoginProcessingRef.current = true;
		setisLogInHidden(true);

		const tenantId = localStorage.getItem(StorageKeys.TIDForAzure);
		if (tenantId == undefined) {
			const loggedIn = await authContext.msalSignIn();

			if (!loggedIn) {
				setErrorMessage(
					intl.formatMessage({ id: "ErrorMessage.SignInAzureAD" })
				);
				isLoginProcessingRef.current = false;
				setisLogInHidden(false);

				return;
			} else {
				const tenantDeployments = await getTenantDeployments();

				if (tenantDeployments && tenantDeployments.length > 1) {
					isLoginProcessingRef.current = false;
					setisLogInHidden(false);

					return;
				}
			}
		}

		getTenantId()
			.then((tenantId: string) => {
				if (!tenantId) {
					setErrorMessage(
						intl.formatMessage({ id: "ErrorMessage.SignInAzureAD" })
					);
					isLoginProcessingRef.current = false;
					setisLogInHidden(false);

					return;
				}

				authContext
					.azureSignIn(tenantId)
					.then((result) => {
						if (!result.isLoggedIn) {
							setErrorMessage(result.errorMessage ?? "");
						}
					})
					.finally(() => {
						isLoginProcessingRef.current = false;
						setisLogInHidden(false);
					});
			})
			.catch((err: any) => {
				captureError(err, "Error when getting tenantId");
				
				if (err instanceof Error) {
					setErrorMessage(err.message);
				} else if (typeof err === "string") {
					setErrorMessage(err);
				} else {
					setErrorMessage(intl.formatMessage({ id: "ErrorMessage.SignInWrongEnvironment" }));					
				}
				
				isLoginProcessingRef.current = false;
				setisLogInHidden(false);
			});
	}, [authContext, deployments]);

	return (
		<div className={classes.container}>
			<img className={classes.logo} src="/logo192.png" alt="CC4Teams logo" />
			<Flex gap={GapSize.Medium} column className={classes.tenantsContainer}>
				{deployments.length > 1 && selectedDeployment && (
					<Dropdown
						inlinePopup
						defaultValue={`${selectedDeployment.deployment.name}: ${selectedDeployment.tenantName}`}
						defaultSelectedOptions={[
							`${selectedDeployment.deployment.id}:${selectedDeployment.tenantId}:${selectedDeployment.azureTenantId}`,
						]}
						placeholder={`${selectedDeployment.deployment.name}: ${selectedDeployment.tenantName}`}
						onOptionSelect={(_, selectedOption) =>
							onDeploymentSelectHandler(selectedOption.optionValue)
						}
					>
						{deployments.map((x) => (
							<Option
								key={`${x.deployment.id}:${x.tenantId}:${x.azureTenantId}`}
								text={`${x.deployment.name}: ${x.tenantName}`}
								value={`${x.deployment.id}:${x.tenantId}:${x.azureTenantId}`}
							>
								{`${x.deployment.name}: ${x.tenantName}`}
							</Option>
						))}
					</Dropdown>
				)}
				{isLoading ? (
					<Spinner
						size="tiny"
						label={intl.formatMessage({ id: "Common.Loading" })}
					/>
				) : (
					<Button
						appearance="primary"
						onClick={() => signInHandler()}
						className={classes.signinButton}
						disabled={isLogInHidden}
					>
						{intl.formatMessage({ id: "AuthView.AzureSignIn" })}
					</Button>
				)}
				{!!errorMessage && (
					<Text className={classes.errorText} children={errorMessage} />
				)}
			</Flex>

			<a
				target="_blank"
				rel="noopener noreferrer"
				href="https://contactcenter4all.com/home/contact/"
				className={classes.contactUrl}
			>
				{intl.formatMessage({ id: "AuthView.ContactUsText" })}
			</a>
		</div>
	);
};
